import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Select } from 'chakra-react-select';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import {
  ProviderCausePayoutStatusSchema,
  type ProviderCausePayoutStatus,
} from '@duke-hq/svc-api-db/schemas/enums/ProviderCausePayoutStatus.schema';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  cause: NonNullable<
    RouterOutputs['finances']['providerCausesReport']['listProviderCauses']['items'][number]
  >;
};

export function ProviderCausesModal({ cause, isOpen, onClose }: Props) {
  const utils = trpcAdmin.useUtils();
  const toast = useToast();

  const updateCausePayout =
    trpcAdmin.finances.providerCausesReport.updateProviderCausePayout.useMutation({
      async onSuccess() {
        toast({
          status: 'success',
          title: t`Payment details have been updated`,
        });

        resetFieldsAndClose();
        await utils.finances.providerCausesReport.invalidate();
      },
      onError() {
        toast({
          status: 'error',
          title: t`An error occurred while updating the payment details`,
        });
      },
    });

  const methods = useForm<{
    status: ProviderCausePayoutStatus;
    notes: string;
  }>({
    defaultValues: {
      status: cause.status,
      notes: cause.notes ?? '',
    },
  });
  const {
    clearErrors,
    reset,
    watch,
    formState: { errors },
  } = methods;

  const currentStatus = watch('status');
  const hasStatusChanged = currentStatus !== cause.status;

  const resetFieldsAndClose = () => {
    reset();
    clearErrors();
    onClose();
  };

  return (
    <Modal
      size="2xl"
      isOpen={isOpen}
      onClose={() => {
        resetFieldsAndClose();
      }}
    >
      <ModalOverlay />
      <ModalContent
        padding={4}
        minH={'300px'}
        as={'form'}
        onSubmit={methods.handleSubmit(async (data) => {
          await updateCausePayout.mutateAsync({
            causeId: cause.cause.id,
            providerDrn: cause.providerDrn,
            notes: data.notes,
            status: data.status,
          });
        })}
      >
        <ModalHeader>
          <Trans>Payment Details</Trans>
          <ModalCloseButton margin={4} />
        </ModalHeader>

        <ModalBody pb={6} h="full">
          <FormProvider {...methods}>
            <VStack gap="8px" w="full" py="8px">
              <HStack w="full" justify="space-between" align="start" gap="10px">
                <FormControl id="category" isInvalid={!!errors.status} isRequired>
                  <FormLabel htmlFor="category" fontWeight="medium" fontSize="sm">
                    <Trans>Payment status</Trans>
                  </FormLabel>
                  <Controller
                    name="status"
                    control={methods.control}
                    render={({ field }) => (
                      <Select
                        inputId="category"
                        name="category"
                        placeholder={t`Please select`}
                        value={
                          field.value
                            ? {
                                value: field.value,
                                label: field.value,
                              }
                            : null
                        }
                        options={Object.values(ProviderCausePayoutStatusSchema.enum).map(
                          (status) => ({
                            value: status,
                            label: status,
                            isDisabled: status === cause.status,
                          }),
                        )}
                        onChange={(val: { value: ProviderCausePayoutStatus } | null) => {
                          field.onChange(val?.value);
                          clearErrors();
                        }}
                      />
                    )}
                  />
                  <FormErrorMessage>{errors.status?.message}</FormErrorMessage>
                </FormControl>
              </HStack>
            </VStack>
            <VStack>
              <FormControl isInvalid={!!errors.notes?.message}>
                <FormLabel
                  color="fg.emphasis"
                  fontWeight="medium"
                  fontSize="sm"
                  mb={1}
                  htmlFor="text"
                >
                  <Trans>Notes</Trans>
                </FormLabel>
                <Controller
                  name="notes"
                  control={methods.control}
                  render={({ field }) => (
                    <Textarea id="text" height="auto" rows={5} mb={2} {...field} />
                  )}
                />
                <FormErrorMessage mb="2">{errors.notes?.message}</FormErrorMessage>
              </FormControl>
            </VStack>
          </FormProvider>
        </ModalBody>
        <ModalFooter gap={4}>
          <Button type="button" variant="ghost" colorScheme="black" onClick={resetFieldsAndClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button type="submit" isDisabled={!hasStatusChanged}>
            <Trans>Update payment</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
